import { Avatar, Popover, Row, Typography } from 'antd';
import anybodyImg from 'assets/icon-anybody.png';
import clsx from 'clsx';
import Box from 'components/Box';
import Button from 'components/Button';
import Text from 'components/Text';
import paymentActions from 'features/payment/services/actions';
import userActions from 'features/user/services/actions';
import { CHECK_IN_TYPES } from 'features/user/services/constants';
import { IWaitingListResItem } from 'features/user/services/types/waiting';
import { customerEditorRef } from 'features/user/widgets/WaitingList/CustomerEditor';
import { get } from 'lodash';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import uiSelector from 'services/UI/selectors';
import { useAppDispatch } from 'store/hooks';
import { styled } from 'styled-components';
import { formatDuration } from 'utils/time';
import call_icon from './call-action.svg';
import { CustomerInfoPopoverUI, IconInfo, useCustomerInfoPopover } from './components/CustomerName';
import sand_clock from './sand_clock.svg';

type IProps = {
  data: IWaitingListResItem;
  viewOnly?: boolean;
  removeMode?: boolean;
  onRemoveTicket: () => void;
  onClick?: () => void;
  onCalling?: () => void;
  active?: boolean;
  displayCallPhoneStillWaiting?: boolean;
  onChangeStaff?: () => void;
};

const Circle = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10' fill='none'>
    <circle cx='5' cy='5' r='5' fill='#FF8890' />
  </svg>
);

const WalkInItem = ({ onCalling = () => undefined, active, data, viewOnly, removeMode, onRemoveTicket, onClick: onPropClick, displayCallPhoneStillWaiting, onChangeStaff }: IProps) => {
  const isAppointment = data.checkInType === CHECK_IN_TYPES.appointment;
  const currentTime = uiSelector.getCurrentTime();
  const navigate = useNavigate();
  // const setting = shopSelectors.data.allSetting();
  const enablePrintCheckIn = true;
  const dispatch = useAppDispatch();
  const countDown = useMemo(() => {
    if (!data) return null;
    const now = moment(currentTime);
    const minutes = now.diff(moment(data.time, 'hh:mm A'), 'minutes');
    return formatDuration(minutes);
  }, [currentTime, data]);

  const onClick = () => {
    if (viewOnly) return;
    if (!data) return;
    onChangeStaff?.();
  };

  const handlePrint = (e: any) => {
    e.stopPropagation();
    // @ts-ignore
    dispatch(paymentActions.getInfoPrintWaiting.fetch({ body: JSON.stringify({ id: data.checkInId, type: 'WALK_IN' }) }));
  };

  if (!data) return null;

  return (
    <Styled onClick={onClick} type='button'>

      <StyledBox className={clsx(isAppointment ? 'appointment' : '', !!data.staffId && 'walk-in-with-staff', active ? 'active' : '',)}>
        <WaitingCustomerHeader>
          <TicketNumber variant='BODY_2' color='text_3' style={{ fontSize: '1.7rem' }}>#{data.ticketNumber}</TicketNumber>
          <CustomerBox id={data.customerId || ''} extraData={data} />
          {displayCallPhoneStillWaiting && <button onClick={e => [e.stopPropagation(), onCalling()]} type='button'><img src={call_icon} alt="call_icon" style={{ width: 40, height: 40 }} /></button>}
        </WaitingCustomerHeader>

        <Row className='box-time' justify={!enablePrintCheckIn ? 'center' : 'space-between'}>
          <Text variant='H9' style={{ fontSize: '1rem', fontWeight: '700' }}>{data.time}</Text>
          <SandClockBox>
            <img src={sand_clock} alt="sand_clock" style={{ width: 25, height: 24 }} />
            <div className='label'>{countDown}</div>
          </SandClockBox>
        </Row>
        <Box alignSelf='center' display='flex' alignItems='center'>
          <div style={{ flex: 1, position: 'relative' }}>
            <Text className='service-name'>
              <p className='text-overflow'>{data.serviceInfo.map(o => o.itemName).join(',') || '--'}</p>
            </Text>
          </div>
          <ChangeStaffButtonStyled onClick={(e: any) => [e.stopPropagation(), e.preventDefault()]} type='button'>
            <div>
              <Avatar size={40} className="circle-avatar" src={data?.avatar || anybodyImg} style={{ color: '#1D2129' }}>{get(data?.staffName, [0], 'Any')}</Avatar>
              <p>{data?.staffName|| 'Any Body'}</p>
            </div>
          </ChangeStaffButtonStyled>
          {removeMode ?
            <Row justify={'center'} className='remove-row'>
              <Button icon='deleteIconCashier' size='small' height={2} style={{ height: '2rem', padding: '0 4px' }} onClick={e => [e.stopPropagation(), e.preventDefault(), onRemoveTicket()]} />
            </Row>
            :
            !!enablePrintCheckIn && <Row justify={'center'}><Button icon='print' size='small' height={2} style={{ height: '2rem', padding: '4px' }} onClick={handlePrint} /></Row>
          }
        </Box>
      </StyledBox>
    </Styled>
  );
};

export default WalkInItem;
type CustomerBoxProps = {
  id: string;
  extraData: IWaitingListResItem;
}
const CustomerBox = ({ id, extraData }: CustomerBoxProps) => {
  const dispatch = useAppDispatch();
  const { customerInfo, customerDetailLoading, viewCustomer, open, setOpen, handleCheckPassBeforeView, dataPin } = useCustomerInfoPopover(id);

  const onOpenChange = (val: boolean) => handleCheckPassBeforeView(val);
  const onEdit = () => {
    customerEditorRef.current?.openWithId(customerInfo.customerId, {
      addon: customerInfo, onSuccess: (data) => {
        if (data) {
          dispatch(userActions.syncCustomerInfo(data));
        }
      }
    });
    setOpen(false);
  };

  return (
    <WrapperInfoBox>
      <div className='content'>
        <Popover
          placement='rightTop'
          title={null}
          content={
            <div onClick={(e) => e.stopPropagation()}>
              <CustomerInfoPopoverUI loading={customerDetailLoading} customerInfo={customerInfo} onOpenChange={onOpenChange} onEdit={onEdit} dataPin={dataPin} />
            </div>
          }
          open={open}
          trigger={'click'}
          onOpenChange={onOpenChange}
          overlayInnerStyle={{ padding: 16, borderRadius: 8, background: '#E4F4F6' }}
        >
          <button style={{ flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', gap: '2px' }} type='button' onClick={viewCustomer}>
            {!!id && <IconInfo />}
            <Text variant='H7' color='text_3' className='text-overflow'>
              {extraData.customerName || 'Unknown'}
            </Text>
          </button>
        </Popover>
      </div>
    </WrapperInfoBox>
  );
};
const WrapperInfoBox = styled.div`
  flex: 1;
  position: relative;
  align-self: stretch;
  .content {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
  }
`;
const ChangeStaffButtonStyled = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
`;

const Styled = styled.button`
  width: 100%;
  // height: 100%;
`;
const StyledBox = styled.div`
padding: 3px 8px;
gap: 0.5rem;
flex-shrink: 0;
width: 100%;
border-radius: 10px;
border: 1px solid var(--line-line-3, #86909C);
background: #FFF;
  .box-time {
    color: #1D2129;
    text-align: center;
    margin: 0 -8px;
    padding: 0 8px;
    font-weight: 700;
    display: flex;
    align-items: center;
  }
  .service-name {
    color: #1D2129;
    display: flex;
    align-items: center;
    text-align: left;
    max-width: 100%;
    position: absolute;
    inset: 0;
  }

  &.active {
    box-shadow: inset #1eb99d 0 0 0 3px, rgba(201, 211, 219, 0.5) 20px 14px 24px;
  }

  &.walk-in-with-staff {
    border-radius: 4px;
    border: 1px solid #A95A23;
    background: #FFFDED;
    .box-time {
      background: #FDF4CE;
    }
  }

  &.appointment {
    border-radius: 4px;
    border: 1px solid #6FABB6;
    background: #C6E9EF;
    .box-time {
      background: #E4F4F6;
    }
  }

`;

const TicketNumber = styled(Text)`
  display: inline-flex;
  padding: 1px 4px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 5px;
  background: #1D2129;
  color: white;
`;

const WaitingCustomerHeader = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const TypographyEllipsis = styled(Typography.Paragraph)`
  color: #1d2129;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 30px;
  text-transform: uppercase;
`;

const SandClockBox = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  .label {
    color: #D2464F;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 30px; /* 166.667% */
    text-transform: capitalize;
  }
`;